import {
  Connection,
  EnrichedConnection,
  ListingNewWithDetailsDict,
} from "../types";

export type ConnectionApprovalFilter = 
  | "fully_approved"      // Both sides must be approved (for public network display)
  | "subject_approved"    // Only subject approval required (for "I'm making" tab)
  | "all";                // No approval filtering (for admin views)

export function enrichConnections(
  id: string | undefined,
  connections: Connection[],
  listingsWithDetails: ListingNewWithDetailsDict,
  approvalFilter: ConnectionApprovalFilter = "fully_approved"
): EnrichedConnection[] {
  if (!id || !connections || !listingsWithDetails) return [];

  // Find all connections where this listing ID appears (either side)
  const listingConnections = connections.filter(
    (conn) => conn.subjectListingId === id || conn.objectListingId === id
  );

  // For each connection, create an enriched object with the connected listing and metadata
  const enriched = listingConnections.map((conn): EnrichedConnection | null => {
    // Apply approval filtering based on the filter type
    if (approvalFilter === "fully_approved") {
      // Both sides must be approved for the connection to be visible
      if (conn.subjectApprovalStatus !== "approved" || conn.objectApprovalStatus !== "approved") {
        return null;
      }
    } else if (approvalFilter === "subject_approved") {
      // Only subject approval required (useful for showing what someone created)
      if (conn.subjectApprovalStatus !== "approved") {
        return null;
      }
    }
    // "all" filter shows everything regardless of approval status

    // Determine if current listing is the subject
    const isSubject = conn.subjectListingId === id;

    // Get the "other" listing ID (the connected listing)
    const connectedListingId = isSubject
      ? conn.objectListingId
      : conn.subjectListingId;

    // Look up the full listing details
    const connectedListing = listingsWithDetails[connectedListingId];

    if (!connectedListing) return null;

    return {
      listing: connectedListing,
      connection: {
        roleType: conn.roleType,
        roleLabel: conn.roleLabel,
        isSubject,
        subjectApprovalStatus: conn.subjectApprovalStatus,
        objectApprovalStatus: conn.objectApprovalStatus,
      },
    };
  });

  const filteredEnriched = enriched.filter(
    (ec): ec is EnrichedConnection => ec !== null
  );

  return filteredEnriched;
}
